// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * NextOfKinModePage - /nabestaanden
 * Minimale pilotwaardigheid: twee tabs.
 * Tab 1 "Wat nu?" - uitvaartwensen, contacten, bank, testamentlocatie
 * Tab 2 "Persoonlijk" - READY berichten, welkomstwoord
 *
 * Laadt data met eigenaar-userId (matchedKey.userId via sessionStore).
 * Rechtencheck via PermissionService.resolveAccess().
 * Alleen lezen - geen schrijftoegang.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Phone, CreditCard, FileText, Flower2, Mail, Smartphone,
  Heart, AlertTriangle, CheckCircle2, Clock, LogOut, ShieldCheck,
  Globe, BookOpen, TrendingUp,
  PawPrint, KeyRound, Stethoscope, Briefcase, Users, CalendarClock, ChevronRight, Car,
} from 'lucide-react'
import { Card }        from '@/components/ui/Card'
import { LoadingState } from '@/components/feedback/LoadingState'
import { useSessionStore } from '@/store'
import { resolveAccess } from '@/services/permission'
import { ROUTES } from '@/lib/constants/routes'
import { loadFuneralWishes }  from '@/services/wishes'
import { loadMedicalWishes }  from '@/services/wishes'
import { loadBank, loadSecondHome, loadInvestments, loadTestamentDetails,
         loadPets, loadInsurance, loadPension,
         type SecondHomeData, type InvestmentsData, type TestamentDetails,
         type PetsData, type InsuranceData, type PensionData } from '@/services/wizard'
import { loadNotarial, type NotarialData } from '@/services/notarial'
import { loadVehicles } from '@/services/vehicles'
import type { VehicleData } from '@/types/vehicle'
import { loadDocuments, loadMessages } from '@/services/wishes'
import { loadTrustedPersons, type TrustedPersonConfig } from '@/services/trustedPerson'
import { getFirstProfile }    from '@/services/profile'
import { loadPersonalFarewell, type PersonalFarewellData, loadDigitalEstate, type DigitalEstateData } from '@/services/wishes'
import type { FuneralWishesData, MedicalWishesData, DocumentEntry, MessageDraft } from '@/services/wishes'
import type { BankData } from '@/services/wizard'

// ── Types ──────────────────────────────────────────────────────────────────────

interface LoadedData {
  ownerName:        string
  funeral:          FuneralWishesData | null
  medical:          MedicalWishesData | null
  bank:             BankData | null
  documents:        DocumentEntry[]
  messages:         MessageDraft[]
  farewellNote:     string
  digitalEstate:    DigitalEstateData | null
  secondHome:       SecondHomeData | null
  notarial:         NotarialData | null
  testamentDetails: TestamentDetails | null
  investments:      InvestmentsData | null
  pets:             PetsData | null
  insurance:        InsuranceData | null
  pension:          PensionData | null
  vehicles:         VehicleData | null
  trustedPersons:   TrustedPersonConfig[]
  permissions: {
    funeral:   boolean
    medical:   boolean
    bank:      boolean
    documents: boolean
    messages:  boolean
    digital:   boolean
  }
}

// ── Hulpfuncties ───────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <div className="flex flex-col gap-0.5 py-2.5 border-b border-sand-100 last:border-0">
      <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-ink leading-relaxed">{value}</p>
    </div>
  )
}

// Detecteert telefoonnummer in waarde en toont bel-knop (indigo)
function PhoneRow({ label, value }: { label: string; value: string }) {
  if (!value) return null
  // Telefoonnummer: bevat minstens 7 cijfers (internationaal of nationaal)
  const digits = value.replace(/[^\d]/g, '')
  const isPhone = digits.length >= 7
  const telHref = `tel:${value.replace(/\s/g, '')}`
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-sand-100 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider">{label}</p>
        <p className="text-sm text-ink leading-relaxed break-words">{value}</p>
      </div>
      {isPhone && (
        <a href={telHref} aria-label={`Bel ${value}`}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-500 text-white px-3 py-1.5 text-xs font-medium hover:bg-indigo-600 active:bg-indigo-700 transition-colors flex-shrink-0">
          <Phone className="h-3.5 w-3.5" aria-hidden="true" />
          Bellen
        </a>
      )}
    </div>
  )
}

function SectionCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sage-50 text-sage-600 flex-shrink-0">
          {icon}
        </div>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
      </div>
      {children}
    </Card>
  )
}

interface OvCardData { icon: React.ReactNode; title: string; detail: string; tel?: string; goTab?: 'now' | 'personal' }

function UrgencyGroup({
  tone, icon, title, subtitle, cards, onGo,
}: {
  tone: 'alert' | 'warn' | 'calm'
  icon: React.ReactNode; title: string; subtitle: string
  cards: OvCardData[]; onGo: (t: 'now' | 'personal') => void
}) {
  if (!cards.length) return null
  const head = tone === 'alert' ? 'text-status-alertText' : tone === 'warn' ? 'text-status-warnText' : 'text-sage-700'
  const ring = tone === 'alert' ? 'border-status-alertBorder' : tone === 'warn' ? 'border-status-warnBorder' : 'border-sage-200'
  const chip = tone === 'alert' ? 'bg-status-alertBg' : tone === 'warn' ? 'bg-status-warnBg' : 'bg-sage-50'
  return (
    <div className={`rounded-2xl border ${ring} bg-white p-4`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${chip} ${head} flex-shrink-0`}>{icon}</div>
        <div>
          <h3 className={`text-base font-semibold ${head}`}>{title}</h3>
          <p className="text-xs text-ink-4">{subtitle}</p>
        </div>
      </div>
      <ul className="flex flex-col gap-2.5">
        {cards.map((c, i) => (
          <li key={i} className="flex items-start gap-2.5 rounded-xl border border-sand-100 bg-white px-3 py-2.5">
            <span className={`mt-0.5 ${head} flex-shrink-0`}>{c.icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">{c.title}</p>
              <p className="text-sm text-ink-3 leading-relaxed">{c.detail}</p>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {c.tel && (
                  <a href={`tel:${c.tel.replace(/\s/g, '')}`}
                     className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-600 transition-colors">
                    <Phone className="h-3.5 w-3.5" aria-hidden="true" /> Bellen
                  </a>
                )}
                {c.goTab && (
                  <button type="button" onClick={() => onGo(c.goTab!)}
                    className="inline-flex items-center gap-1 rounded-lg border border-sand-300 px-2.5 py-1 text-xs font-medium text-ink-3 hover:bg-sand-50 transition-colors">
                    Bekijken <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Hoofdcomponent ─────────────────────────────────────────────────────────────

export function NextOfKinModePage() {
  const { userId, sessionRole, clearSession } = useSessionStore()
  const navigate = useNavigate()

  const handleEndSession = useCallback(() => {
    clearSession()
    navigate(ROUTES.NEXT_OF_KIN_ENTRY, { replace: true })
  }, [clearSession, navigate])
  const [tab,       setTab]       = useState<'overview' | 'now' | 'personal'>('overview')
  const [data,      setData]      = useState<LoadedData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!userId) return

    async function load() {
      // Rol bepalen voor rechtencheck
      const role = sessionRole === 'TRUSTED_PERSON' ? 'TRUSTED_PERSON' : 'NEXT_OF_KIN'

      // VP-permissies ophalen voor rechtencheck
      let storedPerms = {
        moduleFuneralWishes:    false,
        moduleMedicalWishes:    false,
        moduleDocumentIndex:    false,
        moduleMessages:         false,
        moduleDigitalEstate:    false,
      }
      try {
        const vps = await loadTrustedPersons(userId!)
        const activeVP = vps.find(v => v.confirmationStatus !== 'REVOKED')
        if (activeVP) {
          storedPerms = {
            moduleFuneralWishes:    activeVP.moduleFuneralWishes,
            moduleMedicalWishes:    activeVP.moduleMedicalWishes,
            moduleDocumentIndex:    activeVP.moduleDocumentIndex,
            moduleMessages:         activeVP.moduleMessages,
            moduleDigitalEstate:    activeVP.moduleDigitalEstate ?? false,
          }
        }
      } catch { /* geen VP record - standaard rechten gelden */ }

      const canFuneral   = resolveAccess('moduleFuneralWishes',  role, storedPerms.moduleFuneralWishes)
      const canMedical   = resolveAccess('moduleMedicalWishes',  role, storedPerms.moduleMedicalWishes)
      const canDocuments = resolveAccess('moduleDocumentIndex',  role, storedPerms.moduleDocumentIndex)
      const canMessages  = resolveAccess('moduleMessages',       role, storedPerms.moduleMessages)
      const canDigital   = resolveAccess('moduleDigitalEstate',  role, storedPerms.moduleDigitalEstate)

      // Naam eigenaar
      const profile = await getFirstProfile()
      const ownerName = profile?.firstName ?? 'de overledene'

      // Data laden op basis van rechten
      const [funeral, medical, bank, documents, messages, farewell, digitalEstate,
             secondHome, notarial, testamentDetails, investments,
             pets, insurance, pension, vehicles, trustedPersons] = await Promise.all([
        canFuneral   ? loadFuneralWishes(userId!) : Promise.resolve(null),
        canMedical   ? loadMedicalWishes(userId!) : Promise.resolve(null),
        canFuneral   ? loadBank(userId!)          : Promise.resolve(null),
        canDocuments ? loadDocuments(userId!)     : Promise.resolve([]),
        canMessages  ? loadMessages(userId!)      : Promise.resolve([]),
        loadPersonalFarewell(userId!),
        canDigital   ? loadDigitalEstate(userId!) : Promise.resolve(null),
        // V44: tweede woning, notarieel, beleggingen - altijd laden indien documents-recht
        canDocuments ? loadSecondHome(userId!)       : Promise.resolve(null),
        canDocuments ? loadNotarial(userId!)          : Promise.resolve(null),
        canDocuments ? loadTestamentDetails(userId!)  : Promise.resolve(null),
        canDocuments ? loadInvestments(userId!)       : Promise.resolve(null),
        // V49-DEMO: praktische bronnen voor het overzicht (zelfde documents-recht)
        canDocuments ? loadPets(userId!)              : Promise.resolve(null),
        canDocuments ? loadInsurance(userId!)         : Promise.resolve(null),
        canDocuments ? loadPension(userId!)           : Promise.resolve(null),
        canDocuments ? loadVehicles(userId!)          : Promise.resolve(null),
        loadTrustedPersons(userId!),
      ])

      // Persoonlijk welkomstwoord
      const farewellNote = (farewell as PersonalFarewellData | null)?.generalMessage ?? ''

      setData({
        ownerName,
        funeral,
        medical,
        bank,
        documents:        Array.isArray(documents) ? documents : [],
        messages:         Array.isArray(messages)  ? messages  : [],
        farewellNote,
        digitalEstate:    digitalEstate    as DigitalEstateData | null,
        secondHome:       secondHome       as SecondHomeData   | null,
        notarial:         notarial         as NotarialData     | null,
        testamentDetails: testamentDetails as TestamentDetails | null,
        investments:      investments      as InvestmentsData  | null,
        pets:             pets             as PetsData         | null,
        insurance:        insurance        as InsuranceData    | null,
        pension:          pension          as PensionData      | null,
        vehicles:         vehicles         as VehicleData      | null,
        trustedPersons:   Array.isArray(trustedPersons) ? trustedPersons : [],
        permissions: {
          funeral:   canFuneral,
          medical:   canMedical,
          bank:      canFuneral,
          documents: canDocuments,
          messages:  canMessages,
          digital:   canDigital,
        },
      })
      setIsLoading(false)
    }

    load()
  }, [userId, sessionRole])

  if (isLoading) return <LoadingState message="Laden..." />
  if (!data)     return <LoadingState message="Laden..." />

  const { funeral, medical, bank, documents, messages, permissions, digitalEstate, secondHome, notarial, testamentDetails, investments, pets, insurance, pension, vehicles, trustedPersons } = data
  const readyMessages = messages.filter(m => m.status === 'READY')

  // Testament-locatie
  const testamentDoc = documents.find(d =>
    d.label?.toLowerCase().includes('testament') ||
    d.category === 'LEGAL'
  )
  // Uitvaartverzekering
  const uitvaartVerzDoc = documents.find(d =>
    d.label?.toLowerCase().includes('uitvaart') ||
    d.label?.toLowerCase().includes('verzekering')
  )

  // ── V49-DEMO: overzicht-kaarten automatisch bepaald uit aanwezige data ──────────
  type OvCard = OvCardData

  const direct: OvCard[] = []
  const hasLeverage    = !!investments?.investments?.some(i => i.hasLeverage === 'yes')
  const hasInvestments = investments?.hasInvestments === 'yes' || (investments?.investments?.length ?? 0) > 0
  if (hasLeverage) {
    direct.push({ icon: <TrendingUp className="h-4 w-4" />, title: 'Hefboomproducten geregistreerd',
      detail: 'Er zijn hefboomproducten geregistreerd die snel in waarde kunnen veranderen. Bekijk de gegevens en neem zo nodig contact op met de broker of een financieel adviseur.', goTab: 'now' })
  } else if (hasInvestments) {
    direct.push({ icon: <TrendingUp className="h-4 w-4" />, title: 'Actieve beleggingen',
      detail: 'Er zijn beleggingen geregistreerd. Bekijk de gegevens en neem zo nodig contact op met de broker of een financieel adviseur.', goTab: 'now' })
  }
  const petList    = pets?.pets ?? []
  const petsNoCare = petList.filter(p => !p.caretaker?.trim())
  if (petList.length) {
    direct.push({ icon: <PawPrint className="h-4 w-4" />,
      title:  petsNoCare.length ? 'Huisdier zonder vastgelegde verzorger' : 'Huisdier verzorgen',
      detail: petsNoCare.length
        ? `${petsNoCare.length} huisdier(en) zonder vastgelegde verzorger. Regel zo snel mogelijk opvang.`
        : `${petList.length} huisdier(en) geregistreerd; zorg voor verzorging en opvang.` })
  }
  const kh = secondHome?.localContact
  if (secondHome?.hasSecondHome === 'yes' && (kh?.name?.trim() || secondHome.documents?.keys?.trim())) {
    direct.push({ icon: <KeyRound className="h-4 w-4" />, title: 'Sleutelhouder en woningtoegang',
      detail: kh?.name?.trim()
        ? `${kh.name}${kh.role ? ' - ' + kh.role : ''}${secondHome.city ? ' (' + secondHome.city + ')' : ''}`
        : (secondHome.documents?.keys ?? ''),
      tel: kh?.phone?.trim() || undefined })
  }
  if (permissions.funeral && funeral?.type) {
    direct.push({ icon: <Flower2 className="h-4 w-4" />, title: 'Directe uitvaartzaken',
      detail: 'Uitvaartwensen zijn vastgelegd. Neem contact op met de uitvaartverzorger om de uitvaart in gang te zetten.', goTab: 'now' })
  }
  // Onderneming - zakelijke gegevens zitten in de notariële blob (geen aparte opslag)
  const hasBusiness = !!(notarial && (notarial.bedrijfsnaam?.trim() || notarial.handelsnaam?.trim() ||
    notarial.kvkNummer?.trim() || notarial.rechtsvorm?.trim()))
  if (permissions.documents && hasBusiness) {
    const bedrijf = notarial!.bedrijfsnaam?.trim() || notarial!.handelsnaam?.trim() || ''
    const samenvatting = [
      bedrijf && `${bedrijf}${notarial!.rechtsvorm?.trim() ? ' (' + notarial!.rechtsvorm.trim() + ')' : ''}`,
      notarial!.kvkNummer?.trim()      && `KvK ${notarial!.kvkNummer.trim()}`,
      notarial!.ondernemingBank?.trim() && `zakelijke rekening: ${notarial!.ondernemingBank.trim()}`,
    ].filter(Boolean).join(' · ')
    direct.push({ icon: <Briefcase className="h-4 w-4" />, title: 'Onderneming geregistreerd',
      detail: `Er zijn zakelijke gegevens vastgelegd${samenvatting ? ' (' + samenvatting + ')' : ''}. ` +
              'Controleer of er lopende verplichtingen, zakelijke rekeningen of administratieve acties zijn die aandacht vragen.',
      tel: notarial!.ondernemingTelefoon?.trim() || undefined })
  }

  const days: OvCard[] = []
  if (permissions.funeral && (funeral?.funeralHomeContact?.trim() || funeral?.funeralHomePhone?.trim())) {
    days.push({ icon: <Flower2 className="h-4 w-4" />, title: 'Uitvaartverzorger',
      detail: funeral.funeralHomeContact?.trim() || 'Uitvaartverzorger', tel: funeral.funeralHomePhone?.trim() || undefined })
  }
  if (permissions.medical && (medical?.gpName?.trim() || medical?.gpPractice?.trim())) {
    days.push({ icon: <Stethoscope className="h-4 w-4" />, title: 'Huisarts',
      detail: [medical.gpName, medical.gpPractice].filter(v => v?.trim()).join(' - '), tel: medical.gpPhone?.trim() || undefined })
  }
  if (permissions.bank && (bank?.accounts?.length ?? 0) > 0) {
    days.push({ icon: <CreditCard className="h-4 w-4" />, title: 'Bank',
      detail: `${bank!.accounts.length} bankrekening(en) geregistreerd; meld het overlijden bij de bank.`, goTab: 'now' })
  }
  const uitvaartVerz = insurance?.insurances?.find(i => i.uitvaartType?.trim() || i.type?.toLowerCase().includes('uitvaart'))
  if (uitvaartVerz) {
    days.push({ icon: <ShieldCheck className="h-4 w-4" />, title: 'Uitvaartverzekering',
      detail: [uitvaartVerz.insurer || uitvaartVerz.insurerOther, uitvaartVerz.policyNumber].filter(v => v?.trim()).join(' - ') || 'Uitvaartverzekering geregistreerd.' })
  }
  const otherIns = (insurance?.insurances?.length ?? 0) - (uitvaartVerz ? 1 : 0)
  if (otherIns > 0) {
    days.push({ icon: <ShieldCheck className="h-4 w-4" />, title: 'Verzekeringen', detail: `${otherIns} verzekering(en) geregistreerd.` })
  }
  if (pension?.hasPension === 'yes') {
    days.push({ icon: <Briefcase className="h-4 w-4" />, title: 'Pensioen en werkgever',
      detail: 'Pensioen geregistreerd; informeer het pensioenfonds en de werkgever en vraag nabestaandenpensioen aan.' })
  }
  const activeVPs = trustedPersons.filter(v => v.confirmationStatus !== 'REVOKED')
  if (activeVPs.length) {
    days.push({ icon: <Users className="h-4 w-4" />, title: 'Belangrijke contactpersonen',
      detail: activeVPs.map(v => `${v.firstName} (${v.relationship})`).join(', '), goTab: 'now' })
  }

  const later: OvCard[] = []
  if (permissions.funeral && funeral)             later.push({ icon: <Flower2 className="h-4 w-4" />,    title: 'Uitvaartwensen',          detail: 'Bekijk de vastgelegde uitvaartwensen.', goTab: 'now' })
  if (permissions.documents && documents.length)  later.push({ icon: <FileText className="h-4 w-4" />,   title: 'Documenten (Documentwijzer)', detail: `${documents.length} document(en) met vindplaats.`, goTab: 'now' })
  if (permissions.medical && medical)             later.push({ icon: <Heart className="h-4 w-4" />,      title: 'Medische wensen',          detail: 'Bekijk de medische wensen en het levenstestament.', goTab: 'personal' })
  if (permissions.documents && notarial)          later.push({ icon: <BookOpen className="h-4 w-4" />,   title: 'Notariële wensen',         detail: 'Bekijk executeur, voogdij en notariële contacten.', goTab: 'now' })
  if (permissions.messages && readyMessages.length) later.push({ icon: <Mail className="h-4 w-4" />,     title: 'Persoonlijke berichten',   detail: `${readyMessages.length} bericht(en) klaar.`, goTab: 'personal' })
  if (permissions.digital && digitalEstate)       later.push({ icon: <Smartphone className="h-4 w-4" />, title: 'Digitale nalatenschap',    detail: 'Bekijk telefoon en digitale toegang.', goTab: 'now' })
  if (secondHome?.hasSecondHome === 'yes')        later.push({ icon: <Globe className="h-4 w-4" />,      title: 'Woning',                   detail: 'Bekijk de gegevens van de woning.', goTab: 'now' })
  if ((vehicles?.vehicles?.length ?? 0) > 0)      later.push({ icon: <Car className="h-4 w-4" />,        title: 'Voertuigen',               detail: `${vehicles!.vehicles.length} voertuig(en) geregistreerd.` })

  return (
    <div className="flex flex-col gap-0 max-w-2xl mx-auto">

      {/* Header met sessie-beëindiging */}
      <div className="mx-4 mt-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-sage-100">
            <ShieldCheck className="h-4 w-4 text-sage-700" />
          </div>
          <div>
            <p className="text-xs font-semibold text-ink">Vertrouwenspersoon-portaal</p>
            <p className="text-xs text-ink-4">{data.ownerName}</p>
          </div>
        </div>
        <button
          onClick={handleEndSession}
          className="flex items-center gap-1.5 rounded-xl border border-status-alertBorder bg-status-alertBg px-3 py-2 text-xs font-semibold text-status-alertText hover:opacity-80 transition-opacity flex-shrink-0"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sessie beëindigen
        </button>
      </div>

      {/* Demo-disclaimer */}
      <div className="mx-4 mt-3 flex items-start gap-3 rounded-xl border border-status-warnBorder bg-status-warnBg px-4 py-3">
        <AlertTriangle className="h-4 w-4 text-status-warnText flex-shrink-0 mt-0.5" />
        <p className="text-xs text-status-warnText">
          <strong>Demo:</strong> u bekijkt de wensen van {data.ownerName}. Alle berichten zijn voorbeelden en worden niet echt verstuurd.
        </p>
      </div>

      {/* Welkomstwoord */}
      {data.farewellNote && (
        <div className="mx-4 mt-4 rounded-2xl bg-sage-50 border border-sage-200 px-5 py-4">
          <p className="text-xs font-semibold text-sage-600 mb-1.5">Persoonlijk woord van {data.ownerName}</p>
          <p className="text-sm text-ink leading-relaxed italic">"{data.farewellNote}"</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-sand-200 mt-4 mx-4">
        {([
          { key: 'overview', label: 'Overzicht',   icon: <Clock    className="h-4 w-4" /> },
          { key: 'now',      label: 'Wat nu?',     icon: <Flower2  className="h-4 w-4" /> },
          { key: 'personal', label: 'Persoonlijk',  icon: <Heart    className="h-4 w-4" /> },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? 'border-sage-600 text-sage-700'
                : 'border-transparent text-ink-4 hover:text-ink-3'
            }`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* Tab 0 - Overzicht (eerste scherm na toegang) */}
      {tab === 'overview' && (
        <div className="flex flex-col gap-4 p-4">

          {/* Gecondoleerd */}
          <div className="rounded-2xl bg-gradient-to-br from-moss to-sage-700 p-6 text-white shadow-elevated">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="h-5 w-5" aria-hidden="true" />
              <h2 className="text-xl font-semibold">Gecondoleerd</h2>
            </div>
            <p className="text-sm text-white/90 leading-relaxed">
              Allereerst wensen wij u veel sterkte met dit verlies. {data.ownerName} heeft tijdens het
              leven belangrijke wensen en informatie vastgelegd om u te helpen bij de afhandeling.
              Hieronder ziet u eerst welke zaken aandacht vragen.
            </p>
          </div>

          {direct.length === 0 && days.length === 0 && later.length === 0 && (
            <div className="rounded-2xl border border-sand-200 bg-white px-5 py-6 text-center">
              <p className="text-sm text-ink-3">Er is nog geen informatie vrijgegeven om hier te tonen.</p>
            </div>
          )}

          <UrgencyGroup tone="alert" icon={<Clock className="h-5 w-5" />}
            title="Direct aandacht" subtitle="Kan financiële of praktische gevolgen hebben"
            cards={direct} onGo={setTab} />
          <UrgencyGroup tone="warn" icon={<CalendarClock className="h-5 w-5" />}
            title="Binnen enkele dagen" subtitle="Meldingen en eerste contacten"
            cards={days} onGo={setTab} />
          <UrgencyGroup tone="calm" icon={<FileText className="h-5 w-5" />}
            title="Daarna - het volledige dossier" subtitle="Wensen, documenten en overige onderdelen"
            cards={later} onGo={setTab} />
        </div>
      )}

      {/* Tab 1 - Wat nu? */}
      {tab === 'now' && (
        <div className="flex flex-col gap-4 p-4">

          {/* Uitvaartwensen */}
          {permissions.funeral && funeral ? (
            <SectionCard icon={<Flower2 className="h-4 w-4" />} title="Uitvaartwensen">
              <div className="flex flex-col divide-y divide-sand-100">
                <InfoRow label="Type uitvaart"
                  value={
                    funeral.type === 'burial'    ? 'Begraving' :
                    funeral.type === 'cremation' ? 'Crematie' :
                    funeral.type === 'natural'   ? 'Natuurbegraving' :
                    funeral.type === 'open'      ? 'Geen voorkeur' : ''
                  }
                />
                <InfoRow label="Voorkeurlocatie" value={funeral.locationVenue || funeral.locationWish || ''} />
                <InfoRow label="Uitvaartondernemer" value={funeral.funeralHome || ''} />
                {funeral.funeralHomeContact && (
                  <InfoRow label="Contactpersoon" value={funeral.funeralHomeContact} />
                )}
                {funeral.funeralHomePhone && (
                  <PhoneRow label="Telefoon" value={funeral.funeralHomePhone} />
                )}
                {funeral.funeralHomeNotes && (
                  <InfoRow label="Toelichting" value={funeral.funeralHomeNotes} />
                )}
                <InfoRow label="Plechtigheid"
                  value={
                    funeral.ceremonyType === 'religious' ? 'Religieuze dienst' :
                    funeral.ceremonyType === 'civil'     ? 'Burgerlijke plechtigheid' :
                    funeral.ceremonyType === 'private'   ? 'Alleen familie en vrienden' :
                    funeral.ceremonyType === 'none'      ? 'Geen ceremonie' :
                    funeral.ceremonyType === 'other'     ? (funeral.ceremonyTypeOther || 'Anders') : ''
                  }
                />
                {funeral.ceremonyWishes && (
                  <InfoRow label="Wensen plechtigheid" value={funeral.ceremonyWishes} />
                )}
                {(funeral.musicUrl || funeral.music) && (
                  <InfoRow label="Muziek" value={funeral.musicUrl || funeral.music} />
                )}
                {funeral.flowers && <InfoRow label="Bloemen" value={funeral.flowers} />}
                {funeral.clothing && <InfoRow label="Kleding" value={funeral.clothing} />}
                {funeral.obituaryAd === 'yes' && (
                  <InfoRow label="Rouwadvertentie"
                    value={`Gewenst${funeral.obituaryAdWhere ? ` - ${funeral.obituaryAdWhere === 'regional' ? 'regionale krant' : funeral.obituaryAdWhere === 'national' ? 'landelijke krant' : 'anders'}` : ''}`}
                  />
                )}
                {funeral.specialRequests && (
                  <InfoRow label="Persoonlijke wensen" value={funeral.specialRequests} />
                )}
              </div>
            </SectionCard>
          ) : (
            <Card>
              <div className="flex items-center gap-2 text-ink-4">
                <Flower2 className="h-4 w-4" />
                <p className="text-sm">Uitvaartwensen - geen toegang of niet ingevuld.</p>
              </div>
            </Card>
          )}

          {/* Contacten */}
          <SectionCard icon={<Phone className="h-4 w-4" />} title="Contactpersonen">
            <div className="flex flex-col divide-y divide-sand-100">
              {permissions.medical && medical?.gpName && (
                <InfoRow label="Huisarts" value={`${medical.gpName}${medical.gpContact ? ' - ' + medical.gpContact : ''}`} />
              )}
              {permissions.funeral && funeral?.funeralHome && (
                <InfoRow label="Uitvaartondernemer"
                  value={[
                    funeral.funeralHome,
                    funeral.funeralHomeContact,
                    funeral.funeralHomePhone,
                  ].filter(Boolean).join(' - ')}
                />
              )}
              {!permissions.medical && !permissions.funeral && (
                <p className="text-sm text-ink-4 py-2">Geen contacten beschikbaar.</p>
              )}
            </div>
          </SectionCard>

          {/* Bankrekeningen */}
          {permissions.bank && bank?.accounts?.length ? (
            <SectionCard icon={<CreditCard className="h-4 w-4" />} title="Bankrekeningen">
              <div className="flex flex-col divide-y divide-sand-100">
                {bank.accounts.map((acc, i) => (
                  <InfoRow
                    key={i}
                    label={`${acc.bankName || 'Bank'} - ${acc.accountType || ''}`}
                    value={acc.iban || 'IBAN niet opgegeven'}
                  />
                ))}
              </div>
            </SectionCard>
          ) : null}

          {/* Tweede woning */}
          {permissions.documents && secondHome?.hasSecondHome === 'yes' && (
            <SectionCard icon={<Globe className="h-4 w-4" />} title="Tweede woning">
              <div className="flex flex-col divide-y divide-sand-100">
                {secondHome.country && secondHome.country !== 'NL' && (
                  <InfoRow label="Land" value={
                    secondHome.country === 'IT' ? 'Italië' :
                    secondHome.country === 'ES' ? 'Spanje' :
                    secondHome.country === 'FR' ? 'Frankrijk' :
                    secondHome.country === 'DE' ? 'Duitsland' :
                    secondHome.country === 'BE' ? 'België' :
                    secondHome.country
                  } />
                )}
                {secondHome.city && <InfoRow label="Plaats" value={secondHome.city} />}
                {secondHome.type && (
                  <InfoRow label="Type" value={
                    secondHome.type === 'vacation'   ? 'Vakantiewoning' :
                    secondHome.type === 'recreation' ? 'Recreatiewoning' :
                    secondHome.type === 'apartment'  ? 'Appartement' :
                    secondHome.typeOther || secondHome.type
                  } />
                )}
                {secondHome.localContact?.name && (
                  <InfoRow label="Lokaal contact" value={[
                    secondHome.localContact.name,
                    secondHome.localContact.role,
                  ].filter(Boolean).join(' - ')} />
                )}
                {secondHome.localContact?.phone && (
                  <PhoneRow label="Telefoon contact" value={secondHome.localContact.phone} />
                )}
                {secondHome.mortgageProvider && secondHome.hasMortgage === 'yes' && (
                  <InfoRow label="Hypotheekverstrekker" value={
                    secondHome.mortgageProvider === 'Anders'
                      ? secondHome.mortgageProviderOther || 'Zie documentenwijzer'
                      : secondHome.mortgageProvider
                  } />
                )}
                {secondHome.mortgagePhone && <PhoneRow label="Telefoon hypotheek" value={secondHome.mortgagePhone} />}
                {secondHome.documents?.keys && <InfoRow label="Sleutels" value={secondHome.documents.keys} />}
                {secondHome.documents?.ownership && <InfoRow label="Eigendomsakte" value={secondHome.documents.ownership} />}
              </div>
            </SectionCard>
          )}

          {/* Notariële contacten */}
          {permissions.documents && (notarial?.executeur?.name || notarial?.voogdij?.name || testamentDetails?.notarisName) && (
            <SectionCard icon={<BookOpen className="h-4 w-4" />} title="Notariële contacten">
              <div className="flex flex-col divide-y divide-sand-100">
                {notarial?.executeur?.name && (
                  <InfoRow label="Executeur" value={notarial.executeur.name} />
                )}
                {notarial?.executeur?.phone && (
                  <PhoneRow label="Telefoon executeur" value={notarial.executeur.phone} />
                )}
                {notarial?.voogdij?.name && (
                  <InfoRow label="Voogd kinderen" value={notarial.voogdij.name} />
                )}
                {notarial?.voogdij?.phone && (
                  <PhoneRow label="Telefoon voogd" value={notarial.voogdij.phone} />
                )}
                {testamentDetails?.notarisName && (
                  <InfoRow label="Notaris testament" value={[
                    testamentDetails.notarisName,
                    testamentDetails.notarisCity,
                  ].filter(Boolean).join(', ')} />
                )}
                {testamentDetails?.documentLocation && (
                  <InfoRow label="Testament locatie" value={testamentDetails.documentLocation} />
                )}
              </div>
            </SectionCard>
          )}

          {/* Beleggingen */}
          {permissions.bank && investments?.hasInvestments === 'yes' && investments.investments?.length > 0 && (
            <SectionCard icon={<TrendingUp className="h-4 w-4" />} title="Beleggingen">
              <div className="flex flex-col divide-y divide-sand-100">
                {investments.investments.map((inv, i) => {
                  const broker = inv.brokerName === 'Anders' ? (inv.brokerNameOther || '-') : inv.brokerName
                  const prefix = investments.investments.length > 1 ? `${i + 1}. ` : ''
                  return (
                    <div key={i} className="py-2.5 last:pb-0 first:pt-0">
                      {broker && <InfoRow label={`${prefix}Broker`} value={broker} />}
                      {inv.investmentType && <InfoRow label={`${prefix}Type`} value={inv.investmentType} />}
                      {inv.hasLeverage === 'yes' && (
                        <div className="flex items-center gap-2 py-1">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                          <p className="text-xs text-amber-700 font-semibold">
                            {prefix}Actieve optie- of hefboomposities - tijdgevoelig
                          </p>
                        </div>
                      )}
                      {inv.isCrypto && inv.cryptoPlatform && (
                        <InfoRow label={`${prefix}Crypto`} value={
                          inv.cryptoPlatform === 'Anders' ? (inv.cryptoPlatformOther || 'Anders') : inv.cryptoPlatform
                        } />
                      )}
                    </div>
                  )
                })}
              </div>
            </SectionCard>
          )}

          {/* Telefoon & digitale toegang */}
          {permissions.digital && digitalEstate && (digitalEstate.hasSmartphone === 'yes' || digitalEstate.authenticatorApp || digitalEstate.passwordManagerApp) && (
            <SectionCard icon={<Smartphone className="h-4 w-4" />} title="Telefoon & digitale toegang">
              <div className="flex flex-col divide-y divide-sand-100">
                {digitalEstate.hasSmartphone === 'yes' && (
                  <>
                    {(digitalEstate.phoneBrand || digitalEstate.phoneModel) && (
                      <InfoRow label="Toestel" value={[digitalEstate.phoneBrand, digitalEstate.phoneModel].filter(Boolean).join(' ')} />
                    )}
                    {digitalEstate.phoneNumber   && <InfoRow label="Telefoonnummer" value={digitalEstate.phoneNumber} />}
                    {digitalEstate.phoneProvider && <InfoRow label="Provider"       value={digitalEstate.phoneProvider} />}
                    {digitalEstate.unlockMethod  && (
                      <InfoRow label="Ontgrendeling"
                        value={digitalEstate.unlockMethod === 'pincode' ? 'Pincode - zie digitale noodenvelop' :
                               digitalEstate.unlockMethod === 'face'    ? 'Face ID' :
                               digitalEstate.unlockMethod === 'fingerprint' ? 'Vingerafdruk' :
                               digitalEstate.unlockMethod === 'pattern' ? 'Patroon - zie digitale noodenvelop' :
                               'Zie digitale noodenvelop'}
                      />
                    )}
                  </>
                )}
                {digitalEstate.authenticatorApp && digitalEstate.authenticatorApp !== 'none' && (
                  <InfoRow label="Authenticator"
                    value={digitalEstate.authenticatorApp === 'microsoft' ? 'Microsoft Authenticator' :
                           digitalEstate.authenticatorApp === 'google'    ? 'Google Authenticator' :
                           digitalEstate.authenticatorApp === 'apple'     ? 'Apple' :
                           digitalEstate.authenticatorApp === 'duo'       ? 'Duo' :
                           digitalEstate.authenticatorApp === 'authy'     ? 'Authy' :
                           'Anders'}
                  />
                )}
                {digitalEstate.authenticatorNotes && (
                  <InfoRow label="Uitleg authenticator" value={digitalEstate.authenticatorNotes} />
                )}
                {digitalEstate.passwordManagerApp && digitalEstate.passwordManagerApp !== 'none' && (
                  <InfoRow label="Wachtwoordmanager"
                    value={digitalEstate.passwordManagerApp === '1password' ? '1Password' :
                           digitalEstate.passwordManagerApp === 'bitwarden' ? 'Bitwarden' :
                           digitalEstate.passwordManagerApp === 'lastpass'  ? 'LastPass' :
                           digitalEstate.passwordManagerApp === 'apple'     ? 'Apple Wachtwoorden' :
                           digitalEstate.passwordManagerApp === 'google'    ? 'Google Password Manager' :
                           'Anders'}
                  />
                )}
                {digitalEstate.passwordManagerLocation && (
                  <InfoRow label="Locatie hoofdwachtwoord" value={digitalEstate.passwordManagerLocation} />
                )}
              </div>
              {digitalEstate.emergencyEnvelopeNotes && (
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Digitale noodenvelop</p>
                  <p className="text-sm text-amber-900 whitespace-pre-wrap font-mono">{digitalEstate.emergencyEnvelopeNotes}</p>
                </div>
              )}
            </SectionCard>
          )}

          {/* Documenten */}
          {permissions.documents && (
            <SectionCard icon={<FileText className="h-4 w-4" />} title="Belangrijke documenten">
              <div className="flex flex-col divide-y divide-sand-100">
                {testamentDoc && (
                  <InfoRow label="Testament" value={testamentDoc.location || 'Locatie niet opgegeven'} />
                )}
                {uitvaartVerzDoc && (
                  <InfoRow label="Uitvaartverzekering" value={uitvaartVerzDoc.location || 'Locatie niet opgegeven'} />
                )}
                {documents.filter(d => d !== testamentDoc && d !== uitvaartVerzDoc).slice(0, 5).map((doc, i) => (
                  <InfoRow key={i} label={doc.label} value={doc.location || ''} />
                ))}
                {documents.length === 0 && (
                  <p className="text-sm text-ink-4 py-2">Geen documenten vastgelegd door gebruiker.</p>
                )}
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* Tab 2 - Persoonlijk */}
      {tab === 'personal' && (
        <div className="flex flex-col gap-4 p-4">

          {/* Berichten */}
          {permissions.messages ? (
            <SectionCard icon={<Mail className="h-4 w-4" />} title="Persoonlijke berichten">
              {readyMessages.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {readyMessages.map(m => (
                    <div key={m.id} className="rounded-xl bg-sage-50 border border-sage-100 px-4 py-3">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-sage-600 flex-shrink-0" />
                        <p className="text-xs font-semibold text-sage-700">{m.recipientLabel}</p>
                      </div>
                      <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{m.content}</p>
                    </div>
                  ))}
                  {messages.filter(m => m.status !== 'READY').length > 0 && (
                    <div className="flex items-center gap-2 rounded-xl bg-sand px-4 py-2.5">
                      <Clock className="h-3.5 w-3.5 text-ink-4 flex-shrink-0" />
                      <p className="text-xs text-ink-4">
                        {messages.filter(m => m.status !== 'READY').length} conceptberichten zijn nog niet gereed gemaakt.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-ink-4">Er zijn geen gereedgemaakte berichten.</p>
              )}
            </SectionCard>
          ) : (
            <Card>
              <p className="text-sm text-ink-4">Berichten - geen toegang ingesteld.</p>
            </Card>
          )}

          {/* Medische wensen indien toegang */}
          {permissions.medical && medical && (
            <SectionCard icon={<Heart className="h-4 w-4" />} title="Medische wensen">
              <div className="flex flex-col divide-y divide-sand-100">
                {medical.resuscitation && (
                  <InfoRow label="Reanimatie"
                    value={
                      medical.resuscitation === 'yes'     ? 'Ja, reanimeer mij' :
                      medical.resuscitation === 'no'      ? 'Niet reanimeren (DNAR)' :
                      medical.resuscitation === 'discuss' ? 'Bespreek met arts' : ''
                    }
                  />
                )}
                {medical.treatmentPreferences && (
                  <InfoRow label="Behandelwensen" value={medical.treatmentPreferences} />
                )}
                {medical.hospicePreference && (
                  <InfoRow label="Voorkeur zorglocatie" value={medical.hospicePreference} />
                )}
              </div>
            </SectionCard>
          )}

        </div>
      )}

      {/* Sessie beëindigen - onderaan */}
      <div className="mx-4 mb-8 mt-6">
        <button
          onClick={handleEndSession}
          className="flex items-center justify-center gap-2 w-full rounded-2xl border-2 border-status-alertBorder bg-status-alertBg px-5 py-4 text-sm font-semibold text-status-alertText hover:opacity-80 transition-opacity"
        >
          <LogOut className="h-4 w-4" />
          Sessie beëindigen
        </button>
        <p className="text-xs text-ink-4 text-center mt-2">
          Na het beëindigen kunt u via browser-terug niet opnieuw toegang krijgen.
        </p>
      </div>

    </div>
  )
}
